import React from "react";
import { ResponsiveBar } from "@nivo/bar";
import TailwindButtonDynamic from "../Layout/TailwindComponents/TailwindButtonDynamic";
import VPOPOutputsSkeleton from "./VPOPOutputsSkeleton";



const ANTIGEN_COLORS = {
    'DTwP-HepB-Hib':     '#FF6B35',
    'DTwP-HepB-Hib-IPV': '#FF9F1C',
    'HPV':               '#A8DADC',
    'IPV':               '#BAE6FD',
    'JE':                '#F4A261',
    'Malaria':           '#DDD6FE',
    'Measles':           '#FBCFE8',
    'MenA':              '#6EE7B7',
    'MenACWXY':          '#34D399',
    'MR':                '#F9A8D4',
    'OCV':               '#FDE68A',
    'PCV':               '#93C5FD',
    'Rota':              '#C4B5FD',
    'TCV':               '#FCA5A5',
    'YF':                '#a9d18e',
};

const getColor = (antigen) => ANTIGEN_COLORS[antigen] ?? '#9CA3AF';

function VPOPOutputs({ countryInfo, aggregatedData, areAllApiCallsComplete }) {
    // Early return with null if no aggregated data at all
    if (!aggregatedData) return null;

    // Show skeleton while API calls are still in progress
    if (!areAllApiCallsComplete) {
        return <VPOPOutputsSkeleton />;
    }

    // Check if we have meaningful data to display
    const hasDisplayableData = () => {
        return (aggregatedData.baseline && aggregatedData.baseline.length > 0) || 
               (aggregatedData.optimized && aggregatedData.optimized.length > 0);
    };

    // If all APIs are complete but no data, don't show anything
    if (!hasDisplayableData()) {
        return null;
    }

    const processData = () => {
        const result = {
            guaranteed:    { baseline: {}, optimized: {} },
            discretionary: { baseline: {}, optimized: {} },
        };
        ['baseline', 'optimized'].forEach(scenario => {
            (aggregatedData[scenario] || []).forEach(dataArray => {
                if (!Array.isArray(dataArray)) return;
                dataArray.forEach(item => {
                    const cat = item.cvb_category === 'Guaranteed' ? 'guaranteed' : 'discretionary';
                    const antigen = item.vaccine;
                    if (!result[cat][scenario][antigen]) {
                        result[cat][scenario][antigen] = { cost_to_country: 0, cost_to_gavi: 0 };
                    }
                    result[cat][scenario][antigen].cost_to_country += item.cost_to_country || 0;
                    result[cat][scenario][antigen].cost_to_gavi    += item.cost_to_gavi    || 0;
                });
            });
        });
        return result;
    };

    const data = processData();

const getBudgetValues = () => {
    const allArrays = [
        ...(aggregatedData.baseline  || []),
        ...(aggregatedData.optimized || []),
    ];
    for (const arr of allArrays) {
        if (Array.isArray(arr) && arr.length > 0) {
            const guaranteed = arr[0].guaranteed_budget;
            const discretionary = arr[0].discretionary_budget;
            if (guaranteed !== null && guaranteed !== undefined) {
                return {
                    guaranteedBudget:    guaranteed    / 1000000,
                    discretionaryBudget: discretionary / 1000000,
                };
            }
        }
    }
    return { guaranteedBudget: null, discretionaryBudget: null };
};
    const { guaranteedBudget, discretionaryBudget } = getBudgetValues();
    
const getCofinancingStatus = () => {
    const allArrays = [
        ...(aggregatedData.baseline  || []),
        ...(aggregatedData.optimized || []),
    ];
    for (const arr of allArrays) {
        if (Array.isArray(arr) && arr.length > 0) {
            const firstRow = Array.isArray(arr[0]) ? arr[0][0] : arr[0];
            if (firstRow?.cofinancing_status != null) {
                return firstRow.cofinancing_status;
            }
        }
    }
    return null;
};

    const cofinancingStatus = getCofinancingStatus();

    const missingGuaranteed = guaranteedBudget == null || guaranteedBudget === 0;
    const missingDiscretionary = discretionaryBudget == null || discretionaryBudget === 0;
    const missingBudgetMessage = (missingGuaranteed || missingDiscretionary)
        ? cofinancingStatus === 'Fully self-financing'
            ? `Note: This country is Fully Self-Financing — Guaranteed and Discretionary budget does not apply.`
            : `Note: This country is missing ${missingGuaranteed && missingDiscretionary ? 'guaranteed and discretionary' : missingGuaranteed ? 'guaranteed' : 'discretionary'} budget data.`
        : null;

    const buildNivoDetailedData = (category, costType) => {
        const allAntigens = new Set([
            ...Object.keys(data[category].baseline),
            ...Object.keys(data[category].optimized),
        ]);
        const baselineRow = { scenario: 'Baseline' };
        const optimizedRow = { scenario: 'Optimized' };
        allAntigens.forEach(antigen => {
            const bVal = (data[category].baseline[antigen]?.[costType] || 0) / 1000000;
            const oVal = (data[category].optimized[antigen]?.[costType] || 0) / 1000000;
            if (bVal > 0) baselineRow[antigen] = bVal;
            if (oVal > 0) optimizedRow[antigen] = oVal;
        });
        return [baselineRow, optimizedRow];
    };

    const buildNivoSimplifiedData = (costType) => {
        const sum = (obj) =>
            Object.values(obj).reduce((acc, v) => acc + (v[costType] || 0), 0) / 1000000;
        const gBase = sum(data.guaranteed.baseline);
        const gOpt  = sum(data.guaranteed.optimized);
        const dBase = sum(data.discretionary.baseline);
        const dOpt  = sum(data.discretionary.optimized);
        return [
            {
                scenario: 'Baseline',
                ...(gBase > 0 ? { Guaranteed: gBase } : {}),
                ...(dBase > 0 ? { Discretionary: dBase } : {}),
            },
            {
                scenario: 'Optimized',
                ...(gOpt > 0 ? { Guaranteed: gOpt } : {}),
                ...(dOpt > 0 ? { Discretionary: dOpt } : {}),
            },
        ];
    };

    const commonProps = (keys, colorFn, maxVal) => ({
        maxValue: maxVal ? maxVal * 1.15 : 'auto',
        indexBy: 'scenario',
        keys,
        groupMode: 'stacked',
        colors: ({ id }) => colorFn(id),
        borderRadius: 0,
        borderWidth: 0,
        padding: 0.1,
        layers: ['grid', 'axes', 'bars', 'markers', 'legends', 'annotations'],
        axisBottom: { tickSize: 0, tickPadding: 8, tickRotation: 0 },
        axisLeft: {
            tickSize: 0,
            tickPadding: 8,
            format: v => `$${v.toFixed(0)}M`,
            tickValues: 5,
        },
        enableLabel: false,
        animate: false,
        tooltip: ({ id, value, color }) => (
            <div style={{
                background: 'white',
                padding: '8px 12px',
                border: '1px solid #e5e7eb',
                borderRadius: 4,
                fontSize: 12,
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            }}>
                <span style={{ display: 'inline-block', width: 10, height: 10, background: color, borderRadius: 2, marginRight: 6 }} />
                <strong>{id}</strong>: ${value.toFixed(1)}M
            </div>
        ),
        theme: {
            grid: { line: { stroke: '#e5e7eb', strokeWidth: 1 } },
            axis: { ticks: { text: { fontSize: 10, fill: '#6b7280' } } },
        },
        margin: { top: 50, right: 95, bottom: 50, left: 50 },
        legends: [{
            dataFrom: 'keys',
            anchor: 'right',
            direction: 'column',
            justify: false,
            translateX: 130,
            translateY: 0,
            itemsSpacing: 4,
            itemWidth: 120,
            itemHeight: 16,
            itemTextColor: '#374151',
            symbolSize: 10,
            symbolShape: 'square',
        }],
    });

    const DetailedNivoChart = ({ category, costType, budgetValue, budgetColor, budgetLabel, label }) => {
        const nivoData = buildNivoDetailedData(category, costType);
        const keys = [...new Set([
            ...Object.keys(data[category].baseline),
            ...Object.keys(data[category].optimized),
        ])].sort();

        // Don't render if no data
        const hasData = nivoData.some(row => keys.some(k => row[k] > 0));
        if (!hasData) return (
            <div className="flex flex-col items-center justify-center" style={{ height: 360 }}>
                <p className="text-xs text-gray-400 italic">No data</p>
            </div>
        );

        const maxVal = Math.max(
            ...nivoData.map(row => keys.reduce((sum, k) => sum + (row[k] || 0), 0)),
            budgetValue ?? 0
        );

        const BudgetLineLayer = ({ yScale, innerWidth }) => {
            const [hovered, setHovered] = React.useState(false);
            if (!budgetValue) return null;
            const y = yScale(budgetValue);
            const tooltipWidth = 180;
            const tooltipHeight = 28;
            const tooltipX = innerWidth / 2 - tooltipWidth / 2;
            const tooltipY = y - tooltipHeight - 6;
            return (
                <g>
                    <line x1={0} x2={innerWidth} y1={y} y2={y} stroke="transparent" strokeWidth={12} style={{ cursor: 'pointer' }} onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)} />
                    <line x1={0} x2={innerWidth} y1={y} y2={y} stroke={budgetColor} strokeWidth={2} strokeDasharray="6 4" pointerEvents="none" />
                    <text x={4} y={y - 6} fill={budgetColor} fontSize={10} fontWeight={600} pointerEvents="none">{budgetLabel}</text>
                    {hovered && (
                        <g pointerEvents="none">
                            <rect x={tooltipX} y={tooltipY} width={tooltipWidth} height={tooltipHeight} rx={4} ry={4} fill="white" stroke={budgetColor} strokeWidth={1} style={{ filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.1))' }} />
                            <text x={tooltipX + tooltipWidth / 2} y={tooltipY + tooltipHeight / 2 + 4} fill={budgetColor} fontSize={11} fontWeight={600} textAnchor="middle" pointerEvents="none">
                                {budgetLabel}: ${budgetValue.toFixed(1)}M
                            </text>
                        </g>
                    )}
                </g>
            );
        };

        const TotalsLayer = ({ bars, yScale }) => {
            const totals = {};
            bars.forEach(bar => {
                const key = bar.data.indexValue;
                totals[key] = (totals[key] || 0) + bar.data.value;
            });
            const seen = {};
            return bars.map(bar => {
                const key = bar.data.indexValue;
                if (seen[key]) return null;
                seen[key] = true;
                const total = totals[key];
                if (!total) return null;
                return (
                    <text
                        key={key}
                        x={bar.x + bar.width / 2}
                        y={yScale(total) - 6}
                        textAnchor="middle"
                        fontSize={10}
                        fontWeight={600}
                        fill="#374151"
                    >
                        ${total.toFixed(1)}M
                    </text>
                );
            });
        };

        return (
            <div className="flex flex-col">
                <div style={{ height: 360 }}>
                    <ResponsiveBar
                        data={nivoData}
                        {...commonProps(keys, getColor, maxVal)}
                        layers={['grid', 'axes', 'bars', 'legends', 'annotations', BudgetLineLayer, TotalsLayer]}
                    />
                </div>
                <p className="text-xs text-gray-500 ml-11">{label}</p>
            </div>
        );
    };

const SimplifiedNivoChart = ({ costType, title }) => {
    const nivoData = buildNivoSimplifiedData(costType);
    const simplifiedColors = { Guaranteed: '#FF6B35', Discretionary: '#6B7280' };

    const keys = ['Guaranteed', 'Discretionary'];

    const TotalsLayer = ({ bars, yScale }) => {
        const totals = {};
        bars.forEach(bar => {
            const key = bar.data.indexValue;
            totals[key] = (totals[key] || 0) + bar.data.value;
        });
        const seen = {};
        return bars.map(bar => {
            const key = bar.data.indexValue;
            if (seen[key]) return null;
            seen[key] = true;
            const total = totals[key];
            if (!total) return null;
            return (
                <text
                    key={key}
                    x={bar.x + bar.width / 2}
                    y={yScale(total) - 6}
                    textAnchor="middle"
                    fontSize={10}
                    fontWeight={600}
                    fill="#374151"
                >
                    ${total.toFixed(1)}M
                </text>
            );
        });
    };

    return (
        <div className="bg-white p-4 border shadow-sm rounded-[var(--border-corner-radius-rounding-03)]">
            <h3 className="text-base font-medium mb-3 text-center text-gray-600">{title}</h3>
            <div style={{ height: 400 }}>
                <ResponsiveBar
                    data={nivoData}
                    {...commonProps(keys, (id) => simplifiedColors[id] ?? '#9CA3AF')}
                    padding={0.55}
                    animate={false}
                    layers={['grid', 'axes', 'bars', 'legends', 'annotations', TotalsLayer]}
                />
            </div>
        </div>
    );
};
    return (
        <div className="mt-06">
        <h2 className="text-xl font-semibold mb-04">Data visuals:</h2>
        {missingBudgetMessage && (
            <p className="text-sm text-amber-600 mb-3 italic">{missingBudgetMessage}</p>
        )}

            <div className="grid grid-cols-2 gap-4 mb-4">
                <div className="bg-white p-4 border shadow-sm rounded-[var(--border-corner-radius-rounding-03)]">
                    <h3 className="text-base font-medium mb-3 text-center text-gray-600">Cost to Gavi</h3>
                    <div className="grid grid-cols-2 gap-2">
                        <DetailedNivoChart category="guaranteed" costType="cost_to_gavi" budgetValue={guaranteedBudget} budgetColor="#FF0000" budgetLabel="Guaranteed budget" label="Guaranteed" />
                        <DetailedNivoChart category="discretionary" costType="cost_to_gavi" budgetValue={discretionaryBudget} budgetColor="#1E40AF" budgetLabel="Discretionary budget" label="Discretionary" />
                    </div>
                </div>
                <div className="bg-white p-4 border shadow-sm rounded-[var(--border-corner-radius-rounding-03)]">
                    <h3 className="text-base font-medium mb-3 text-center text-gray-600">Cost to Country</h3>
                    <div className="grid grid-cols-2 gap-2">
                        <DetailedNivoChart category="guaranteed" costType="cost_to_country" budgetValue={guaranteedBudget} budgetColor="#FF0000" budgetLabel="Guaranteed budget" label="Guaranteed" />
                        <DetailedNivoChart category="discretionary" costType="cost_to_country" budgetValue={discretionaryBudget} budgetColor="#1E40AF" budgetLabel="Discretionary budget" label="Discretionary" />
                    </div>
                </div>
            </div>

            <div className="grid grid-cols-2 gap-4 mb-4">
                <SimplifiedNivoChart costType="cost_to_gavi" title="Cost to Gavi" />
                <SimplifiedNivoChart costType="cost_to_country" title="Cost to Country" />
            </div>

            <div className="mt-4 p-4 bg-gray-50 rounded-[var(--border-corner-radius-rounding-03)]">
                <div className="flex justify-between items-center mb-2">
                    <h4 className="text-md font-medium">Data Summary</h4>
                    <TailwindButtonDynamic
                        variant='secondarySmallSurfaceBrandQuaternary'
                        content={{ name: 'Export to CSV', icon: null }}
                        actionHandler={() => {
                            const allData = [
                                ...(aggregatedData?.baseline || []).flat(),
                                ...(aggregatedData?.optimized || []).flat(),
                            ].sort((a, b) =>
                                a.vaccine.localeCompare(b.vaccine) ||
                                a.type.localeCompare(b.type) ||
                                a.scenario.localeCompare(b.scenario) ||
                                a.year - b.year
                            );
                            const headers = ['Discretionary/Guaranteed', 'Vaccine', 'Routine/Campaign', 'Scenario', 'Year', 'TRS', 'Cost to Gavi', 'Cost to Country'];
                            const rows = allData.map(row => [
                                row.cvb_category ?? '',
                                row.vaccine ?? '',
                                row.type ?? '',
                                row.scenario ?? '',
                                row.year ?? '',
                                row.trs ? Math.round(row.trs) : '',
                                row.cost_to_gavi ?? '',
                                row.cost_to_country ?? '',
                            ]);
                            const csv = [headers, ...rows].map(r => r.join(',')).join('\n');
                            const blob = new Blob([csv], { type: 'text/csv' });
                            const url = URL.createObjectURL(blob);
                            const a = document.createElement('a');
                            a.href = url;
                            a.download = `VPOP_${countryInfo?.ISO ?? 'export'}_${new Date().toISOString().slice(0,10)}.csv`;
                            a.click();
                            URL.revokeObjectURL(url);
                        }}
                    />
                </div>

            </div>
        </div>
    );
}

export default VPOPOutputs;